import { useTranslation } from "react-i18next";
import { AppInput, AppButton } from "@/components/ui";

interface MagicLinkFormProps {
  email: string;
  emailError: string | null;
  isSubmitting: boolean;
  isDisabled: boolean;
  onEmailChange: (email: string) => void;
  onSubmit: (email: string) => void;
}

export function MagicLinkForm({
  email,
  emailError,
  isSubmitting,
  isDisabled,
  onEmailChange,
  onSubmit,
}: MagicLinkFormProps): React.ReactElement {
  const { t } = useTranslation();

  const isFormDisabled = isSubmitting || isDisabled;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isFormDisabled) {
      onSubmit(email);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-3 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          {t("auth.page.title")}
        </h1>
        <p className="text-lg font-light text-foreground-secondary">{t("auth.page.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <AppInput
          type="email"
          required
          placeholder={t("auth.magicLink.emailPlaceholder")}
          aria-label={t("auth.magicLink.emailAriaLabel")}
          aria-invalid={emailError ? "true" : undefined}
          aria-describedby={emailError ? "email-error" : undefined}
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          disabled={isFormDisabled}
          className="w-full bg-surface border border-border rounded-2xl py-5 px-6 text-lg placeholder:text-foreground-muted shadow-none outline-none focus:ring-2 focus:ring-success/30 transition-shadow"
        />
        {emailError && (
          <p id="email-error" className="text-sm text-danger" role="alert">
            {emailError}
          </p>
        )}

        <AppButton
          type="submit"
          isPending={isSubmitting}
          isDisabled={isFormDisabled}
          className="w-full h-auto py-5 rounded-2xl bg-success text-background font-bold hover:bg-foreground transition-all shadow-lg shadow-success/20"
          aria-busy={isSubmitting ? "true" : undefined}
        >
          {t("auth.magicLink.submit")}
        </AppButton>
      </form>
    </div>
  );
}
